import React, { useState } from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';
import { showCallDialog } from 'flow/actions/call-dialog-actions';
import { getConnectedMediaDeviceOfKindWithId } from 'flow/utils/media-utils';
import {
    getPreferredAudioOutputVolume,
    savePreferredAudioOutputVolume,
} from 'flow/utils/browser-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';

import { routes } from 'flow/types/routes';
import { useHistory, useParams } from 'react-router-dom';

import Page from 'flow/components/Setting/Page';
import RouteLink from 'flow/components/ui-components/RouteLink';
import SettingSection from 'flow/components/Setting/SettingSection';
import Button from 'flow/components/ui-components/Button';
import NoiseSuppression from './NoiseSuppression';
import SettingRow from './SettingRow';
import RangeSlider from 'flow/components/ui-components/RangeSlider';
import { filterMediaDevicesBySpecificKind } from 'flow/utils/media-utils';
import { ConnectedMediaDevice } from 'flow/types/Headset';
import { generatePath } from 'flow/utils/router-utils';

const AudioInOut = () => {
    const dispatch = useAppDispatch();
    const history = useHistory();
    const { i18next } = useAppContext();

    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const connectedMediaDevices = useAppSelector((state) =>
        state.headsets.get('connectedMediaDevices'),
    );

    const preferredMicrophone = useAppSelector((state) =>
        getConnectedMediaDeviceOfKindWithId(
            connectedMediaDevices,
            'audioinput',
            state.headsets.get('preferredMicrophoneId'),
        ),
    );

    const selectedMicrophone =
        preferredMicrophone ||
        filterMediaDevicesBySpecificKind(
            connectedMediaDevices,
            'audioinput',
        ).find(
            (microphone: ConnectedMediaDevice) =>
                microphone.deviceId === 'default',
        );

    const preferredAudioOutput = useAppSelector((state) =>
        getConnectedMediaDeviceOfKindWithId(
            connectedMediaDevices,
            'audiooutput',
            state.headsets.get('preferredAudioOutputId'),
        ),
    );

    const selectedAudioOutput =
        preferredAudioOutput ||
        filterMediaDevicesBySpecificKind(
            connectedMediaDevices,
            'audiooutput',
        ).find(
            (audioOutput: ConnectedMediaDevice) =>
                audioOutput.deviceId === 'default',
        );

    const audioReference = useAppSelector((state) =>
        state.phone.get('audioReference'),
    );

    const outputVolumeD = audioReference
        ? audioReference.volume
        : getPreferredAudioOutputVolume();
    const [outputVolume, setOutputVolume] = useState(outputVolumeD);

    const sliderFriendlyOutputVolume = outputVolume * 100;

    const handleChangeOutputVolume = (value: number) => {
        const formattedValue = value / 100;

        if (audioReference) {
            audioReference.volume = formattedValue;
        }

        savePreferredAudioOutputVolume(formattedValue);

        setOutputVolume(formattedValue);
    };

    return (
        <Page
            header="settings.softphone.audio_device"
            onClickBack={() =>
                history.push(
                    generatePath(routes['settings.softphone'], {
                        extensionKey,
                    }),
                )
            }
        >
            <SettingSection>
                {i18next.t('media.settings.audioinput')}
            </SettingSection>

            <RouteLink
                to={routes['settings.softphone.audio-in-out.microphone']}
                className="py-4"
                description={selectedMicrophone?.label}
            >
                {i18next.t('media.settings.microphone')}
            </RouteLink>

            <NoiseSuppression />

            <SettingSection>
                {i18next.t('media.settings.audiooutput')}
            </SettingSection>

            <RouteLink
                to={routes['settings.softphone.audio-in-out.audio-output']}
                description={selectedAudioOutput?.label}
                className="py-4"
            >
                {i18next.t('media.settings.speaker')}
            </RouteLink>
            <SettingRow
                cta={
                    <RangeSlider
                        handleChange={(e) => {
                            handleChangeOutputVolume(
                                parseFloat(e.target.value),
                            );
                        }}
                        preferredValue={sliderFriendlyOutputVolume}
                        ariaLabel={i18next.t('media.settings.volume')}
                    />
                }
            >
                {i18next.t('media.settings.volume')}
            </SettingRow>
            <Button
                onClick={() => dispatch(showCallDialog('0046102098693'))}
                className="my-4"
                color="ghost"
            >
                {i18next.t('media.settings.testcallbutton')}
            </Button>
            <p className="text-sm text-gray-400">
                {i18next.t('media.settings.testcalldescription')}
            </p>
        </Page>
    );
};

export default AudioInOut;
